import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import { File as FileModel, SiteFile } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { FileUploadModalComponent } from 'src/app/modals/file-upload-modal/file-upload-modal.component';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-site-files',
  templateUrl: './site-files.component.html',
  styleUrl: './site-files.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SiteFilesComponent implements OnInit {
  constructor(
    private SiteService: SiteService,
    private ModalService: NgbModal,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private UtilityService: UtilityService
  ) {}

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['download', 'delete']
  };

  SiteId?: number;
  reloadSiteFileGridData: boolean = false;
  SuppliedQueryData?: GenericObject;

  uploadFile() {
    const { componentInstance, result } = this.ModalService.open(
      FileUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, {
      helperText: 'A maximum of 20MB of files can be uploaded at once.'
    });

    from(result).subscribe({
      next: (Files) => {
        const SiteFiles = Files.map(
          (File: FileModel) => new SiteFile({ SiteId: this.SiteId, File })
        );

        this.SiteService.saveSiteFiles(SiteFiles).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Site files have been added.'
            );
            this.reloadSiteFileGridData = true;
          }
        });
      }
    });
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SiteFile;
  }) {
    const { SiteId, FileId } = rowData;

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { backdrop: 'static', centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this file?'
      });

      from(result).subscribe({
        next: (response) => {
          if (response) {
            this.SiteService.deleteSiteFile({ SiteId, FileId }).subscribe({
              next: () => {
                this.reloadSiteFileGridData = true;
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted file.'
                );
              }
            });
          }
        }
      });
    }

    if (action === 'download') {
      if (!FileId) {
        throw this.ToastService.addErrorToast('Error', 'Missing file id.', {
          returnError: true
        });
      }

      this.SiteService.getSite(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id'],
          id: SiteId,
          SiteFile: {
            options: { required: true },
            SiteId,
            FileId,
            File: {
              id: FileId,
              options: { required: true }
            }
          }
        })
      ).subscribe({
        next: (Site) => {
          if (!Site) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing site information.',
              { returnError: true }
            );
          }

          const { id: SiteId, SiteFiles } = Site;

          if (!SiteId) {
            throw this.ToastService.addErrorToast('Error', 'Missing site id.', {
              returnError: true
            });
          }

          if (SiteFiles.length > 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Multiple site files were loaded.',
              { returnError: true }
            );
          }

          const {
            File: { filepath, filename, filetype }
          } = SiteFiles[0];

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.SiteService.getSiteS3PresignedUrl(SiteId, FileId).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        }
      });
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const SiteId = ParamMap.get('SiteId');

        if (!SiteId) {
          throw this.ToastService.addErrorToast('Error', 'Missing site id.', {
            returnError: true
          });
        }

        Object.assign(this, {
          SiteId,
          SuppliedQueryData: {
            attributes: ['FileId'],
            SiteId
          }
        });
      }
    });
  }
}
